/**
 * FiscalizacionForm.jsx — Formulario dinámico que renderiza campos según la
 * plantilla del solution_type de la OT. Móvil-friendly (v2.8.0 · Fase 1).
 */
import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Camera, CheckCircle, Loader2, PenLine, Upload, FileText, Zap, Package, Save, Send } from "lucide-react";
import { useAuth } from "../lib/auth";
import SkuSelector from "./SkuSelector";

/* v97.4.0 · Abre el PDF con un enlace firmado de corta duración (no expone el token de sesión) */
async function openFiscalPdf(sid, download = false) {
  // v101.93.0 · download=true → descarga (Content-Disposition: attachment); si no, se abre para ver
  const win = download ? null : window.open("", "_blank");
  try {
    const { data } = await api.get(`/v1/fiscalizacion/submissions/${sid}/pdf-link`, { params: download ? { download: 1 } : {} });
    const url = data?.url || `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${data?.path || ""}`;
    if (download) {
      const a = document.createElement("a");
      a.href = url; a.rel = "noreferrer"; document.body.appendChild(a); a.click(); a.remove();
    } else if (win) win.location.href = url; else window.open(url, "_blank");
  } catch (e) {
    if (win) win.close();
    toast.error(e?.response?.data?.detail || "No se pudo abrir el PDF");
  }
}

export default function FiscalizacionForm({ otId, solutionTypeId }) {
  const [loading, setLoading] = useState(true);
  const [definition, setDefinition] = useState([]);
  const [stInfo, setStInfo] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submissionId, setSubmissionId] = useState(null);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [defRes, subRes] = await Promise.all([
          solutionTypeId ? api.get(`/v1/fiscalizacion/definitions/${solutionTypeId}`) : Promise.resolve({ data: null }),
          api.get(`/v1/fiscalizacion/submissions/by-ot/${otId}`),
        ]);
        if (defRes.data) {
          setDefinition(defRes.data.definition || []);
          setStInfo(defRes.data.solution_type);
        }
        if (subRes.data.submission) {
          setAnswers(subRes.data.submission.answers || {});
          setSubmissionId(subRes.data.submission.id);
          setStatus(subRes.data.submission.status);
        }
      } catch (e) { toast.error("Error cargando formulario"); }
      finally { setLoading(false); }
    })();
  }, [otId, solutionTypeId]);

  const setAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/v1/fiscalizacion/submissions", {
        ot_id: otId, solution_type_id: solutionTypeId, answers, status: "draft",
      });
      setSubmissionId(data.id);
      toast.success("Borrador guardado");
    } catch (e) { toast.error("Error guardando"); }
    finally { setSaving(false); }
  };

  const finalize = async () => {
    // Auto-guardar antes de finalizar
    setFinalizing(true);
    try {
      const { data } = await api.post("/v1/fiscalizacion/submissions", {
        ot_id: otId, solution_type_id: solutionTypeId, answers, status: "draft",
      });
      const sid = data.id;
      const res = await api.post(`/v1/fiscalizacion/submissions/${sid}/finalize`);
      toast.success("✅ OT solucionada · pendiente de fiscalización");
      setSubmissionId(sid);
      setStatus("complete");
      // Descargar el PDF · v97.4.0 · enlace firmado (antes ?auth= no lo aceptaba el backend → 401)
      openFiscalPdf(sid);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        toast.error(`Faltan campos: ${detail.missing.join(", ")}`);
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al finalizar");
      }
    } finally { setFinalizing(false); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!definition.length) return <div className="p-6 text-sm text-gray-500 text-center italic">Sin plantilla configurada</div>;

  const isComplete = status === "complete";

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4" data-testid="fiscal-form">
      {/* Header */}
      <div className="bg-[#0A3747] text-white rounded-lg p-3">
        <div className="flex items-center gap-2 text-xs opacity-80">
          <FileText size={14} /> FISCALIZACIÓN · {stInfo?.code}
        </div>
        <div className="text-sm font-semibold">{stInfo?.name}</div>
        {isComplete && (
          <div className="mt-1.5 text-[11px] bg-emerald-600 inline-block px-2 py-0.5 rounded-full font-bold">
            ✓ SOLUCIONADA · PENDIENTE FISCALIZACIÓN
          </div>
        )}
      </div>

      {/* Secciones */}
      {definition.map((section, si) => (
        <div key={si} className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#0A3747] border-b pb-1.5">
            {si + 1}. {section.title}
          </div>
          {(section.fields || []).map((f) => (
            <FieldRenderer key={f.key} field={f} value={answers[f.key]}
              onChange={(v) => setAnswer(f.key, v)} otId={otId} readonly={isComplete}
              solutionTypeId={solutionTypeId} />
          ))}
        </div>
      ))}

      {/* Footer botones */}
      {!isComplete && (
        <div className="sticky bottom-2 bg-white shadow-lg rounded-lg border border-gray-200 p-3 flex gap-2">
          <button onClick={saveDraft} disabled={saving} className="flex-1 py-2.5 rounded bg-slate-500 text-white text-sm font-bold flex items-center justify-center gap-1.5" data-testid="save-draft">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar borrador
          </button>
          <button onClick={finalize} disabled={finalizing} className="flex-1 py-2.5 rounded bg-emerald-600 text-white text-sm font-bold flex items-center justify-center gap-1.5" data-testid="finalize-form">
            {finalizing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Finalizar OT
          </button>
        </div>
      )}
      {isComplete && submissionId && (
        <div className="text-center flex justify-center gap-2">
          <button
            type="button"
            onClick={() => openFiscalPdf(submissionId)}
            className="inline-block px-4 py-2 rounded border border-[#0A3747] text-[#0A3747] text-sm font-bold"
            data-testid="view-pdf"
          >
            <FileText size={14} className="inline mr-1" /> Ver informe PDF
          </button>
          <button
            type="button"
            onClick={() => openFiscalPdf(submissionId, true)}
            className="inline-block px-4 py-2 rounded bg-[#0A3747] text-white text-sm font-bold"
            data-testid="download-pdf"
          >
            <FileText size={14} className="inline mr-1" /> Descargar PDF
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function FieldRenderer({ field, value, onChange, otId, readonly, solutionTypeId }) {
  const commonLabel = (
    <label className="block text-xs font-semibold text-gray-700 mb-1">
      {field.label}
      {field.required && <span className="text-red-500 ml-1">*</span>}
      {field.hint && <div className="text-[10px] font-normal text-gray-500 mt-0.5">{field.hint}</div>}
    </label>
  );

  switch (field.type) {
    case "text":
      return (<div>{commonLabel}
        {field.multiline ? (
          <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readonly}
            rows={3} className="w-full px-2 py-1.5 border rounded text-sm" data-testid={`f-${field.key}`} />
        ) : (
          <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={readonly}
            className="w-full px-2 py-1.5 border rounded text-sm" data-testid={`f-${field.key}`} />
        )}
      </div>);
    case "number":
    case "power_reading":
      return (<div>{commonLabel}
        <input type="text" inputMode="decimal" value={value ?? ""} onChange={(e) => {
          const raw = e.target.value;
          if (raw === "" || raw === "-") { onChange(raw === "" ? null : raw); return; }
          const normalized = raw.replace(",", ".");
          if (/^-?\d*\.?\d*$/.test(normalized)) onChange(normalized);
        }}
          onBlur={(e) => {
            const normalized = (e.target.value || "").replace(",", ".");
            const num = parseFloat(normalized);
            if (!isNaN(num)) onChange(num);
          }}
          disabled={readonly} className="w-full px-2 py-1.5 border rounded text-sm" data-testid={`f-${field.key}`}
          placeholder={field.type === "power_reading" ? "Ej: -18,5 o -18.5" : ""} />
        {field.type === "power_reading" && <div className="text-[10px] text-yellow-700 mt-0.5"><Zap size={10} className="inline" /> Unidad: dBm — acepta coma o punto como decimal</div>}
      </div>);
    case "checkbox":
      return (<label className="flex items-center gap-2 p-1.5 cursor-pointer">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} disabled={readonly}
          className="w-4 h-4" data-testid={`f-${field.key}`} />
        <span className="text-sm">{field.label} {field.required && <span className="text-red-500">*</span>}</span>
      </label>);
    case "photo":
      return <PhotoField field={field} value={value} onChange={onChange} otId={otId} readonly={readonly} label={commonLabel} />;
    case "signature":
      return <SignatureField field={field} value={value} onChange={onChange} otId={otId} readonly={readonly} label={commonLabel} />;
    case "materials":
      return <MaterialsField field={field} value={value} onChange={onChange} readonly={readonly} label={commonLabel} solutionTypeId={solutionTypeId} />;
    default:
      return null;
  }
}

// ── Photo upload ──────────────────────────────────────────────────────────
function PhotoField({ field, value, onChange, otId, readonly, label }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("ot_id", otId);
      fd.append("field_key", field.key);
      const { data } = await api.post("/v1/fiscalizacion/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.path);
      toast.success("Foto subida");
    } catch (err) { toast.error("Error subiendo foto"); }
    finally { setUploading(false); }
  };
  return (<div>{label}
    {value ? (
      <div className="relative inline-block">
        <img src={`${api.defaults.baseURL}/v1/fiscalizacion/file/${value}?auth=${localStorage.getItem("afc_token")}`}
          alt={field.key} className="rounded border max-w-full max-h-48" />
        {!readonly && (
          <button onClick={() => onChange(null)} className="absolute top-1 right-1 bg-red-600 text-white text-xs px-2 py-0.5 rounded" data-testid={`clear-${field.key}`}>×</button>
        )}
      </div>
    ) : !readonly && (
      <>
        <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} className="hidden" data-testid={`f-${field.key}`} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded text-sm flex items-center justify-center gap-2 hover:bg-gray-50">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <><Camera size={14} /> Tomar / Subir foto</>}
        </button>
      </>
    )}
  </div>);
}

// ── Signature Canvas ──────────────────────────────────────────────────────
function SignatureField({ field, value, onChange, otId, readonly, label }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(!!value);

  useEffect(() => {
    if (!canvasRef.current || readonly) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0A3747"; ctx.lineWidth = 2; ctx.lineCap = "round";
    if (value && value.startsWith("data:image")) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = value;
      setHasSignature(true);
    }
    const start = (e) => { drawing.current = true; const p = pos(e, c); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e, c); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasSignature(true); };
    const end = () => { drawing.current = false; };
    c.addEventListener("mousedown", start); c.addEventListener("mousemove", move); c.addEventListener("mouseup", end); c.addEventListener("mouseleave", end);
    c.addEventListener("touchstart", start, { passive: false }); c.addEventListener("touchmove", move, { passive: false }); c.addEventListener("touchend", end);
    return () => { c.removeEventListener("mousedown", start); c.removeEventListener("mousemove", move); c.removeEventListener("mouseup", end); };
  }, [readonly, value]);

  const pos = (e, c) => {
    const r = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };

  const clear = () => {
    const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasSignature(false); onChange(null);
  };
  const commit = () => {
    if (!canvasRef.current) return;
    onChange(canvasRef.current.toDataURL("image/png"));
    toast.success("Firma guardada");
  };

  return (<div>{label}
    {readonly && value ? (
      <img src={value} alt="firma" className="border rounded max-h-32" />
    ) : (
      <div className="border-2 rounded bg-white" style={{ borderColor: hasSignature ? "#059669" : "#94A3B8" }}>
        <canvas ref={canvasRef} width={600} height={180} style={{ width: "100%", touchAction: "none" }} data-testid={`f-${field.key}`} />
        <div className="flex gap-2 p-1 border-t">
          <button type="button" onClick={clear} className="text-[11px] text-red-600" data-testid={`clear-${field.key}`}>Borrar</button>
          <button type="button" onClick={commit} className="text-[11px] text-emerald-700 ml-auto font-bold"><PenLine size={10} className="inline" /> Guardar firma</button>
        </div>
      </div>
    )}
  </div>);
}

// ── Materials picker · v3.2.0 con SkuSelector ─────────────────────────────
function MaterialsField({ field, value, onChange, readonly, label, solutionTypeId }) {
  const { user } = useAuth();
  const [recipe, setRecipe] = useState([]);
  const [loading, setLoading] = useState(false);
  const items = Array.isArray(value) ? value : [];
  const warehouseLocationId = user?.warehouse_location_id || null;

  useEffect(() => {
    if (!solutionTypeId) return;
    setLoading(true);
    api.get(`/v1/mesa/solution-types/${solutionTypeId}/recipe-lines`)
      .then((r) => setRecipe(r.data.items || []))
      .catch(() => { /* fallback silencioso */ })
      .finally(() => setLoading(false));
  }, [solutionTypeId]);

  // Persistir SKU seleccionado por línea de receta (indexado por material_id)
  const byMaterial = new Map(items.filter(it => it.material_id).map(it => [it.material_id, it]));
  const setLine = (materialId, sku) => {
    const arr = items.filter(it => it.material_id !== materialId);
    if (sku) arr.push(sku);
    onChange(arr);
  };

  // Legacy free-text (retro-compat): agregar items sin material_id
  const [newItem, setNewItem] = useState({ product_name: "", product_category: "", qty: 1 });

  if (loading) return (<div>{label}<div className="p-2 flex items-center gap-2 text-xs text-gray-500"><Loader2 size={12} className="animate-spin"/> Cargando receta…</div></div>);

  const hasRecipe = recipe.length > 0;

  return (<div>{label}
    {hasRecipe && (
      <div className="space-y-2 mb-2" data-testid="recipe-sku-list">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-bold flex items-center gap-1">
          <Package size={10}/> Receta · {recipe.length} línea(s) · Bodega #{warehouseLocationId || "—"}
        </div>
        {recipe.map((mat) => (
          <SkuSelector
            key={mat.id}
            material={mat}
            warehouseLocationId={warehouseLocationId}
            value={byMaterial.get(mat.id) || null}
            onChange={readonly ? () => {} : (sku) => setLine(mat.id, sku)}
          />
        ))}
      </div>
    )}

    {/* Legacy: extras manuales (para tipos sin receta o materiales fuera de plantilla) */}
    <details className="mt-2" open={!hasRecipe}>
      <summary className="text-[11px] text-gray-600 cursor-pointer font-semibold">
        {hasRecipe ? "➕ Agregar material extra (fuera de receta)" : "Materiales instalados"}
      </summary>
      <table className="w-full text-xs mt-1">
        <thead className="bg-gray-50"><tr>
          <th className="text-left px-2 py-1">Producto</th>
          <th className="text-left px-2 py-1">Categoría</th>
          <th className="w-16 px-2 py-1">Cant.</th>
          {!readonly && <th className="w-8"></th>}
        </tr></thead>
        <tbody>
          {items.filter(it => !it.material_id).map((it, i) => (
            <tr key={`extra-${i}`} className="border-t"><td className="px-2 py-1">{it.product_name}</td>
              <td className="px-2 py-1">{it.product_category}</td>
              <td className="px-2 py-1 text-center">{it.qty}</td>
              {!readonly && <td><button onClick={() => {
                const idx = items.findIndex(x => x === it);
                onChange(items.filter((_, j) => j !== idx));
              }} className="text-red-600 text-xs">×</button></td>}
            </tr>
          ))}
          {!readonly && (
            <tr className="bg-yellow-50">
              <td><input value={newItem.product_name} onChange={(e) => setNewItem({ ...newItem, product_name: e.target.value })} className="w-full px-1 py-0.5 border rounded text-xs" placeholder="Producto" data-testid={`extra-mat-name-${field.key}`}/></td>
              <td><input value={newItem.product_category} onChange={(e) => setNewItem({ ...newItem, product_category: e.target.value })} className="w-full px-1 py-0.5 border rounded text-xs" placeholder="Categoría" /></td>
              <td><input type="number" value={newItem.qty} onChange={(e) => setNewItem({ ...newItem, qty: parseFloat(e.target.value) || 0 })} className="w-full px-1 py-0.5 border rounded text-xs text-center" /></td>
              <td><button onClick={() => { if (newItem.product_name) { onChange([...items, newItem]); setNewItem({ product_name: "", product_category: "", qty: 1 }); } }} className="text-emerald-700 font-bold text-xs" data-testid={`add-mat-${field.key}`}>+</button></td>
            </tr>
          )}
        </tbody>
      </table>
    </details>
  </div>);
}
